import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy} from '@dnd-kit/sortable';
import {Link, List, Typography, useTheme} from '@mui/material';
import {styled} from '@mui/material/styles';
import {useEffect, useState, useMemo} from 'react';

import {FilterDialogSectionId, SavedFiltersSectionConfig} from 'filters/features/FilterDialog/types';

import {openFilterDialog} from 'filters/features/FilterDialog/emitter';
import {PresetItem} from 'filters/features/FilterDialog/sections/SavedFiltersSection/features/PresetItem';
import {SavedFiltersItem} from 'filters/features/FilterDialog/sections/SavedFiltersSection/features/SavedFiltersItem';
import {SavedFiltersPrimaryAction} from 'filters/features/FilterDialog/sections/SavedFiltersSection/features/SavedFiltersPrimaryAction';
import {SavedFiltersSecondaryAction} from 'filters/features/FilterDialog/sections/SavedFiltersSection/features/SavedFiltersSecondaryAction';
import {useEditedFilterContext} from 'filters/features/FilterDialog/contexts/internal/EditedFilterProvider/EditedFilterProvider';
import {useFilterActionsContext} from 'filters/contexts/FilterActionsProvider/FilterActionsProvider';

import NoResultsIcon from 'components/icons/placeholders/noResults.svg';
import LoadingIndicator from 'components/LoadingIndicator';
import {Placeholder} from 'components/Placeholder';

import {isEqualFilters} from 'modules/decisionsNext/components/BaseFilter/utils/filter';

export const useCreateSavedFiltersSection = (
  config: Omit<SavedFiltersSectionConfig, 'type' | 'savedId' | 'setSavedId'>
): SavedFiltersSectionConfig => {
  const [savedId, setSavedId] = useState<string | null>(null);

  return useMemo(
    () => ({
      savedId,
      setSavedId,
      footer: {
        primaryAction: SavedFiltersPrimaryAction,
        secondaryAction: SavedFiltersSecondaryAction,
        additionalActions: () => [],
      },
      ...config,
      type: 'saved-filters',
    }),
    [config, savedId]
  );
};

const SRoot = styled('div')(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(4),

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const SList = styled(List)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: 0,

  ':not(:last-child)': {
    marginBottom: theme.spacing(6),
  },
}));

const SOverlayWrapper = styled('div')(({theme}) => ({
  background: theme.palette.common.white,
}));

export const SavedFiltersSection = ({savedId, setSavedId}: SavedFiltersSectionConfig) => {
  const {useStoredFilters, useStoredFiltersActions} = useFilterActionsContext();
  const theme = useTheme();
  const [activeId, setActiveId] = useState<string | null>(null);
  const {savedFilters, savedLoading} = useStoredFilters();
  const {deleteSavedFilter, renameSavedFilter, reorderSavedFilters} = useStoredFiltersActions();

  const {filter} = useEditedFilterContext();

  const savedFiltersIds = savedFilters.saved ? savedFilters.saved.map(saved => saved.id) : [];
  const savedFiltersName = savedFilters.saved ? savedFilters.saved.map(saved => saved.name) : [];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    return () => {
      setSavedId(null);
    };
  }, [setSavedId]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;

    if (active.id !== over?.id) {
      const fromIndex = savedFiltersIds.indexOf(String(active.id));
      const toIndex = savedFiltersIds.indexOf(String(over?.id));
      const reordered = arrayMove(savedFiltersIds, fromIndex, toIndex);
      reorderSavedFilters(reordered);
    }

    setActiveId(null);
  };

  if (savedLoading) {
    return (
      <SRoot>
        <LoadingIndicator />
      </SRoot>
    );
  }

  const active = savedFilters.saved?.find(saved => saved.id === activeId);

  return (
    <SRoot>
      {savedFilters.presets && (
        <>
          <Typography variant='subtitle2' color='text.secondary' marginBottom={4}>
            Preset filters
          </Typography>
          <SList data-testid='saved-filters-preset-items'>
            {savedFilters.presets.map(preset => (
              <PresetItem
                key={preset.id}
                preset={preset}
                selected={preset.id === savedId}
                onSelect={() => setSavedId(preset.id)}
              />
            ))}
          </SList>
        </>
      )}

      <Typography variant='subtitle2' color='text.secondary' marginBottom={4}>
        Custom saved filters
      </Typography>

      {savedFilters.saved?.length ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          measuring={{droppable: {strategy: MeasuringStrategy.Always}}}
          onDragEnd={handleDragEnd}
          onDragStart={handleDragStart}
        >
          <SortableContext items={savedFiltersIds} strategy={verticalListSortingStrategy}>
            <SList>
              {savedFilters.saved.map(saved => (
                <SavedFiltersItem
                  key={saved.id}
                  {...saved}
                  isDndActive={!!activeId}
                  selected={saved.id === savedId}
                  onSelect={() => setSavedId(saved.id)}
                  onDelete={() => deleteSavedFilter(saved.id)}
                  onRename={name => renameSavedFilter(saved.id, name)}
                  active={isEqualFilters(saved.filter, filter)}
                  names={savedFiltersName}
                />
              ))}
            </SList>
          </SortableContext>

          <DragOverlay>
            {active && (
              <SOverlayWrapper>
                <SavedFiltersItem
                  {...active}
                  dragOverlay
                  selected={active?.id === savedId}
                  active={isEqualFilters(active.filter, filter)}
                />
              </SOverlayWrapper>
            )}
          </DragOverlay>
        </DndContext>
      ) : (
        <div style={{flex: 1}}>
          <Placeholder
            size='small'
            icon={<NoResultsIcon color={theme.palette.text.secondary} />}
            heading={`You don't have Saved filters`}
            subheading={
              <>
                Filter criteria can be saved in the{' '}
                <Link
                  component='button'
                  color='primary.main'
                  underline='hover'
                  onClick={() => openFilterDialog(FilterDialogSectionId.ActiveFilters)}
                  style={{verticalAlign: 'baseline'}}
                >
                  Active Filters
                </Link>{' '}
                section.
              </>
            }
          />
        </div>
      )}
      {!!savedFilters.duplicates?.length && (
        <>
          <Typography variant='subtitle2' color='text.secondary' marginBottom={4}>
            Duplicate saved filters (remove to clean up)
          </Typography>

          <SList>
            {savedFilters.duplicates?.map(saved => (
              <SavedFiltersItem key={saved.id} {...saved} disabled onDelete={() => deleteSavedFilter(saved.id)} />
            ))}
          </SList>
        </>
      )}
    </SRoot>
  );
};
